import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCHOOL_FOLDERS = [
  {
    id: 'admission-records',
    icon: '📝',
    title: { hi: 'Admission Records', en: 'Admission Records' },
    description: { hi: 'Forms, admission status, entrance exam और seat flow', en: 'Forms, admission status, entrance exam, and seat flow' },
    items: ['Admission forms', 'Entrance exam sheets', 'Seat confirmations'],
    links: [
      { href: '/admission-engine', label: { hi: 'Admission Engine खोलें', en: 'Open Admission Engine' } },
      { href: '/reports', label: { hi: 'Admission Reports', en: 'Admission Reports' } },
    ],
  },
  {
    id: 'student-files',
    icon: '🎓',
    title: { hi: 'Student Files', en: 'Student Files' },
    description: { hi: 'Student profiles, ID proofs, TC, class-wise records', en: 'Student profiles, ID proofs, TC, and class-wise records' },
    items: ['Student profiles', 'ID proofs', 'Transfer certificates'],
    links: [
      { href: '/students', label: { hi: 'Students Module', en: 'Students Module' } },
      { href: '/data-export', label: { hi: 'School Export', en: 'School Export' } },
    ],
  },
  {
    id: 'finance-docs',
    icon: '💳',
    title: { hi: 'Finance & Receipts', en: 'Finance & Receipts' },
    description: { hi: 'Fee receipts, payment proofs, due statements और reconciliation', en: 'Fee receipts, payment proofs, due statements, and reconciliation' },
    items: ['Fee receipts', 'Due statements', 'Payment proofs'],
    links: [
      { href: '/fees', label: { hi: 'Fees Module', en: 'Fees Module' } },
      { href: '/reports', label: { hi: 'Finance Reports', en: 'Finance Reports' } },
    ],
  },
  {
    id: 'staff-compliance',
    icon: '🏛️',
    title: { hi: 'Compliance & Staff', en: 'Compliance & Staff' },
    description: { hi: 'UDISE+, staff documents, inspections और statutory records', en: 'UDISE+, staff documents, inspections, and statutory records' },
    items: ['UDISE files', 'Staff documents', 'Inspection files'],
    links: [
      { href: '/teachers', label: { hi: 'Teachers Module', en: 'Teachers Module' } },
      { href: '/udise', label: { hi: 'UDISE+ खोलें', en: 'Open UDISE+' } },
    ],
  },
  {
    id: 'academic-reports',
    icon: '📚',
    title: { hi: 'Academic Reports', en: 'Academic Reports' },
    description: { hi: 'Attendance, exams, report cards और class performance', en: 'Attendance, exams, report cards, and class performance' },
    items: ['Attendance reports', 'Exam reports', 'Progress cards'],
    links: [
      { href: '/attendance', label: { hi: 'Attendance खोलें', en: 'Open Attendance' } },
      { href: '/exams', label: { hi: 'Exams & Results', en: 'Exams & Results' } },
    ],
  },
  {
    id: 'school-brand-assets',
    icon: '📸',
    title: { hi: 'School Media Assets', en: 'School Media Assets' },
    description: { hi: 'Gallery, school profile files, prospectus और marketing creatives', en: 'Gallery, school profile files, prospectus, and marketing creatives' },
    items: ['Gallery photos', 'Prospectus', 'School profile assets'],
    links: [
      { href: '/settings', label: { hi: 'Gallery & Profile', en: 'Gallery & Profile' } },
      { href: '/social-media', label: { hi: 'Social Media', en: 'Social Media' } },
    ],
  },
];

const PLATFORM_PITCH_DECKS = [
  {
    id: 'investor-pitch',
    icon: '📈',
    title: { hi: 'Investor Pitch Deck', en: 'Investor Pitch Deck' },
    subtitle: { hi: 'Growth metrics, revenue, market size', en: 'Growth metrics, revenue, market size' },
    href: '/api/docs/pitch-deck-investors',
    testId: 'open-investor-pitch-button',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    id: 'school-pitch',
    icon: '🏫',
    title: { hi: 'School Pitch Deck', en: 'School Pitch Deck' },
    subtitle: { hi: 'Features, benefits, principal demo', en: 'Features, benefits, principal demo' },
    href: '/api/docs/pitch-deck-schools',
    testId: 'open-school-pitch-button',
    gradient: 'from-indigo-500 to-violet-500',
  },
];

export default function DocumentsPage({ token, user }) {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const isPlatformAdmin = user?.role === 'platform_admin';
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(isPlatformAdmin);

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadDocs = useCallback(async () => {
    if (!isPlatformAdmin) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/docs/list`, { headers: headers() });
      const data = await res.json();
      setDocuments(data.documents || []);
    } catch (err) {
      console.error(err);
      setDocuments([]);
    }
    setLoading(false);
  }, [headers, isPlatformAdmin]);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6" data-testid="documents-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-800">
            <i className="fa-solid fa-folder-open text-orange-500"></i>
            {language === 'hi' ? 'Documents' : 'Documents'}
          </h1>
          <p className="mt-1 text-sm text-gray-500" data-testid="documents-page-subtitle">
            {isPlatformAdmin
              ? (language === 'hi' ? 'Platform-only pitch decks और internal docs' : 'Platform-only pitch decks and internal docs')
              : (language === 'hi' ? 'स्कूल से जुड़े documents को folder-wise organize करें' : 'Organize school documents folder-wise')}
          </p>
        </div>
      </div>

      {isPlatformAdmin ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {PLATFORM_PITCH_DECKS.map((deck) => (
              <a
                key={deck.id}
                href={deck.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`rounded-3xl bg-gradient-to-br ${deck.gradient} p-6 text-white shadow-lg transition hover:-translate-y-1 hover:shadow-xl`}
                data-testid={deck.testId}
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-2xl">
                    {deck.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-black">{deck.title[language]}</h3>
                    <p className="text-sm text-white/80">{deck.subtitle[language]}</p>
                  </div>
                </div>
                <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">
                  <span>{language === 'hi' ? 'Open Deck' : 'Open Deck'}</span>
                  <i className="fa-solid fa-arrow-up-right-from-square"></i>
                </div>
              </a>
            ))}
          </div>

          <button
            onClick={() => navigate('/documents/competition')}
            className="w-full rounded-3xl bg-gradient-to-br from-slate-900 to-indigo-700 p-6 text-left text-white shadow-lg transition hover:-translate-y-1 hover:shadow-xl"
            data-testid="open-competition-comparison-button"
          >
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/20 text-2xl">📊</div>
              <div>
                <h3 className="text-lg font-black">{language === 'hi' ? 'Competitive Comparison (Honest)' : 'Competitive Comparison (Honest)'}</h3>
                <p className="text-sm text-white/80">{language === 'hi' ? 'सभी बड़े players बनाम शिक्षा मित्र — features, cost, free-tier व gaps' : 'All big players vs Shiksha Mitra — features, cost, free-tier & gaps'}</p>
              </div>
            </div>
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold">
              <span>{language === 'hi' ? 'खोलें' : 'Open'}</span>
              <i className="fa-solid fa-arrow-right"></i>
            </div>
          </button>

          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-4 flex items-center justify-between gap-3">
              <h2 className="text-lg font-bold text-slate-900">
                {language === 'hi' ? `Available platform docs (${documents.length})` : `Available platform docs (${documents.length})`}
              </h2>
            </div>

            {loading ? (
              <div className="flex h-32 items-center justify-center">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange-500 border-t-transparent"></div>
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                {documents.map((doc, index) => (
                  <a
                    key={`${doc.url}-${index}`}
                    href={doc.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-2xl border border-slate-200 p-4 transition hover:border-orange-300 hover:shadow-sm"
                    data-testid={`platform-document-${index}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-orange-50 text-orange-600">
                        <i className="fa-solid fa-file-lines"></i>
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">{doc.name_hi || doc.name}</p>
                        <p className="mt-1 text-sm text-slate-500">{doc.url}</p>
                      </div>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-slate-800 to-orange-600 p-6 text-white shadow-lg" data-testid="school-documents-hero">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/70">
              {language === 'hi' ? 'School Admin Documents' : 'School Admin Documents'}
            </p>
            <h2 className="mt-2 text-3xl font-black">
              {language === 'hi' ? 'अब यहाँ सिर्फ स्कूल से जुड़े document folders रहेंगे' : 'Only school-related document folders live here now'}
            </h2>
            <p className="mt-3 max-w-3xl text-sm text-white/85" data-testid="school-documents-hero-copy">
              {language === 'hi'
                ? 'Investor pitch deck और platform sales docs यहाँ से हटाए गए हैं। इस section में school records, compliance files, fee receipts, student files और academic reports को clear folder structure में रखा गया है।'
                : 'Investor pitch decks and platform sales docs have been removed from this section. This area is now reserved for school records, compliance files, fee receipts, student files, and academic reports.'}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-3">
            {SCHOOL_FOLDERS.map((folder) => (
              <div key={folder.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid={`school-folder-${folder.id}`}>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-orange-50 text-2xl">
                    {folder.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{folder.title[language]}</h3>
                    <p className="text-sm text-slate-500">{folder.description[language]}</p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {folder.items.map((item) => (
                    <span key={item} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700">
                      {item}
                    </span>
                  ))}
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  {folder.links.map((link, index) => (
                    <a
                      key={`${folder.id}-${index}`}
                      href={link.href}
                      className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-sm font-semibold text-orange-700 transition hover:bg-orange-100"
                      data-testid={`${folder.id}-link-${index}`}
                    >
                      {link.label[language]}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
